const targetTypesService = {
  targetTypes: {
    ip: { 
      icon: 'globe', // Font Awesome icon name
      name: 'IP Address',
      placeholder: '192.168.1.1 or 10.0.0.0/24',
      metadataFields: ['criticality', 'environment', 'tags']
    },
    hostname: { 
      icon: 'desktop', // Font Awesome icon name
      name: 'Hostname',
      placeholder: 'server.example.com',
      metadataFields: ['criticality', 'environment', 'tags']
    },
    cloudResource: { 
      icon: 'cloud', // Font Awesome icon name
      name: 'Cloud Resource',
      placeholder: 'arn:aws:s3:::my-bucket or /subscriptions/xxx/resourceGroups/xxx',
      metadataFields: ['platform', 'service', 'region', 'accountId', 'resourceId', 'criticality', 'environment', 'tags']
    },
    apiEndpoint: { 
      icon: 'plug', // Font Awesome icon name
      name: 'API Endpoint',
      placeholder: 'https://api.example.com/v1/users',
      metadataFields: ['method', 'authentication', 'apiVersion', 'baseUrl', 'criticality', 'environment', 'tags']
    },
    container: { 
      icon: 'box', // Font Awesome icon name
      name: 'Container',
      placeholder: 'nginx:latest or my-app-container',
      metadataFields: ['orchestrator', 'image', 'tag', 'namespace', 'criticality', 'environment', 'tags']
    },
    identity: { 
      icon: 'user', // Font Awesome icon name
      name: 'Identity/User',
      placeholder: 'admin@corp.com or CORP\\jdoe',
      metadataFields: ['identityType', 'privilegeLevel', 'domain', 'criticality', 'tags']
    },
    webapplication: {
      icon: 'globe-americas', // Font Awesome icon name
      name: 'Web Application',
      placeholder: 'https://example.com',
      metadataFields: ['technology', 'framework', 'criticality', 'environment', 'tags']
    },
    network: {
      icon: 'network-wired', // Font Awesome icon name
      name: 'Network Segment',
      placeholder: '10.0.0.0/24 or DMZ',
      metadataFields: ['subnet', 'vlan', 'securityGroup', 'criticality', 'environment', 'tags']
    }
  },

  getTargetType(type) {
    return this.targetTypes[type] || this.targetTypes.ip;
  },

  getAllTypes() {
    return Object.keys(this.targetTypes);
  },

  getTypeDisplay(type) {
    const targetType = this.getTargetType(type);
    return `${targetType.name}`;
  },

  getMetadataFieldConfig() {
    return {
      platform: {
        label: 'Cloud Platform',
        type: 'select',
        options: ['AWS', 'Azure', 'GCP', 'DigitalOcean', 'Other']
      },
      service: {
        label: 'Service Type',
        type: 'text',
        placeholder: 'EC2, S3, Lambda, etc.'
      },
      region: {
        label: 'Region',
        type: 'text',
        placeholder: 'us-east-1'
      },
      accountId: {
        label: 'Account ID',
        type: 'text',
        placeholder: '123456789012'
      },
      resourceId: {
        label: 'Resource ID',
        type: 'text',
        placeholder: 'i-1234567890abcdef0'
      },
      method: {
        label: 'HTTP Method',
        type: 'select',
        options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS']
      },
      authentication: {
        label: 'Authentication Type',
        type: 'select',
        options: ['none', 'basic', 'bearer', 'apiKey', 'oauth2', 'custom']
      },
      apiVersion: {
        label: 'API Version',
        type: 'text',
        placeholder: 'v1, v2, 2023-01-01'
      },
      baseUrl: {
        label: 'Base URL',
        type: 'text',
        placeholder: 'https://api.example.com'
      },
      orchestrator: {
        label: 'Container Platform',
        type: 'select',
        options: ['kubernetes', 'docker', 'ecs', 'gke', 'aks', 'eks']
      },
      image: {
        label: 'Container Image',
        type: 'text',
        placeholder: 'nginx'
      },
      tag: {
        label: 'Image Tag',
        type: 'text',
        placeholder: 'latest'
      },
      namespace: {
        label: 'Namespace',
        type: 'text',
        placeholder: 'default'
      },
      identityType: {
        label: 'Identity Type',
        type: 'select',
        options: ['user', 'serviceAccount', 'group', 'role', 'machineIdentity']
      },
      privilegeLevel: {
        label: 'Privilege Level',
        type: 'select',
        options: ['standard', 'admin', 'root', 'system']
      },
      domain: {
        label: 'Domain',
        type: 'text',
        placeholder: 'CORP, example.com'
      },
      applicationName: {
        label: 'Application Name',
        type: 'text',
        placeholder: 'MyWebApp'
      },
      version: {
        label: 'Version',
        type: 'text',
        placeholder: '2.1.0'
      },
      technology: {
        label: 'Technology Stack',
        type: 'text',
        placeholder: 'Node.js, Python, Java'
      },
      framework: {
        label: 'Framework',
        type: 'text',
        placeholder: 'Express, Django, Spring'
      },
      subnet: {
        label: 'Subnet',
        type: 'text',
        placeholder: '10.0.1.0/24'
      },
      vlan: {
        label: 'VLAN',
        type: 'text',
        placeholder: 'VLAN 100'
      },
      securityGroup: {
        label: 'Security Group',
        type: 'text',
        placeholder: 'sg-12345678'
      },
      criticality: {
        label: 'Criticality',
        type: 'select',
        options: ['low', 'medium', 'high', 'critical']
      },
      environment: {
        label: 'Environment',
        type: 'select',
        options: ['development', 'staging', 'production', 'dr', 'test']
      },
      tags: {
        label: 'Tags',
        type: 'tags',
        placeholder: 'Enter tags separated by commas'
      }
    };
  },

  detectTargetType(value) {
    // IP address patterns
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\/\d{1,2})?$/;
    const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4})$/;
    
    // URL pattern
    const urlRegex = /^https?:\/\/.+/i;
    
    // Cloud resource patterns
    const awsArnRegex = /^arn:aws:.+/;
    const azureResourceRegex = /^\/subscriptions\/.+/;
    const gcpResourceRegex = /^projects\/.+/;
    
    // Container pattern
    const containerRegex = /^[a-zA-Z0-9][a-zA-Z0-9_.-]*(?::[a-zA-Z0-9][a-zA-Z0-9_.-]*)?$/;
    
    // Email/identity pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const domainUserRegex = /^[A-Z0-9]+\\[a-zA-Z0-9]+$/i;

    if (ipv4Regex.test(value) || ipv6Regex.test(value)) {
      return 'ip';
    } else if (urlRegex.test(value)) {
      return 'webapplication';
    } else if (awsArnRegex.test(value) || azureResourceRegex.test(value) || gcpResourceRegex.test(value)) {
      return 'cloudResource';
    } else if (emailRegex.test(value) || domainUserRegex.test(value)) {
      return 'identity';
    } else if (containerRegex.test(value) && value.includes(':')) {
      return 'container';
    } else if (value.includes('.') && !value.includes(' ')) {
      return 'hostname';
    } else {
      return 'network'; // Default for unrecognized patterns
    }
  }
};

export default targetTypesService;